import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import { GeofenceMap } from "../components/GeofenceMap";
import { Modal } from "../components/Modal";
import { AttendanceHealthSparkline } from "../components/AttendanceHealthSparkline";
import { useAuth } from "../contexts/AuthContext";
import { usePermission } from "../contexts/AuthContext";
import {
  Plus, Pencil, Trash2, MapPin, FileText, Users as UsersIcon,
  Grid3x3, List as ListIcon, Upload, ScrollText, ExternalLink, Building2, Route, Navigation, Download, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { formatAddress, distinctLocations } from "../lib/address";
import TourStopsModal from "../components/TourStopsModal";
import GeocodeResultsModal from "../components/GeocodeResultsModal";
import SiteDeactivateModal from "../components/SiteDeactivateModal";
import ReportVolumeBadge from "../components/ReportVolumeBadge";
import SiteRecentActivity from "../components/SiteRecentActivity";
import { EmailListInput } from "../components/EmailListInput";

// Effective bill rate for a site. Site override > agency default > $28.
// Returns { rate:number, source: "site" | "agency" } so the UI can badge
// whether the rate is customized or inherited.
function effectiveBillRate(site, agencyDefaultRate) {
  const own = site?.bill_rate_per_hour;
  if (own != null && own !== "" && Number.isFinite(Number(own))) {
    return { rate: Number(own), source: "site" };
  }
  return { rate: Number(agencyDefaultRate) || 28, source: "agency" };
}

const emptySite = {
  name: "", client_id: "", client_name: "",
  portfolio_id: null,
  address: "",
  street_address: "", city: "", state: "", zip_code: "",
  contact_person: "", contact_phone: "",
  guards_required: 1, contract_value: 0, status: "active",
  bill_rate_per_hour: "",
  lat: "", lng: "", geofence_radius_m: 200,
  timezone: "",
  deactivation_date: "",
  post_orders_text: "",
  gate_codes: "", lockbox_codes: "", access_fobs: "", keys_available: "",
  poc_manager_contact: "", post_orders_other: "",
  supervisor_ids: [],
  tardy_notifications_enabled: false,
  tardy_notification_emails: [],
  dar_mode: "default",
  weekly_digest_enabled: false,
  weekly_digest_recipients: [],
  geofence_notifications_enabled: false,
  notify_portal_user_ids: [],
  enabled_reports: { incidents: true, dar: true, maintenance: true, tours: true },
  notify_portal_users_on_reports: false,
  email_scan_log_limit: null,
  email_photo_limit: null,
  status_check_interval_minutes: null,
  breaks_enabled: true,
  paid_break_max_minutes: null,
  all_breaks_unpaid: false,
  default_paid_break_minutes: null,
  default_unpaid_break_minutes: null,
  break_deduction_threshold_hours: null,
  auto_start_shifts: false,
  auto_start_delay_minutes: 0,
  missed_tour_check_enabled: false,
  missed_tour_check_interval_minutes: 30,
  missed_tour_min_scans_per_window: 0,
  missed_tour_alert_on_zero: true,
  missed_tour_notification_emails: [],
  allow_adhoc_shifts: false,
  allow_unverified_clock_in: false,
  auto_approve_adhoc: null,  // null = inherit agency default; true/false = override
  // ─── Temp job support (Phase 1) ────────────────────────────
  // When true, the site is a short-lived engagement (event, HQ move,
  // film shoot). Requires temp_end_date; auto-archives 48h after that
  // date. Rendered with a TEMP pill on the mobile Available Sites list.
  is_temporary: false,
  temp_end_date: "",
  // Set for Flavour ① (walk-in requester) — mutually exclusive with
  // client_id. Sourced from `/api/adhoc-requesters`.
  adhoc_requester_id: "",
  // Only meaningful when is_temporary=true AND under a real client
  // (Flavour ②). Controls whether the client's client_admin/regional/
  // site_manager see this temp site in the portal.
  portal_visible: true,
};

export default function Sites() {
  const { user } = useAuth();
  const canCreateSite = usePermission("sites:create");
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  // Only admin / super_admin / OM / CSO see the cross-site attendance-health column.
  // Client / site users are explicitly excluded so cross-site stats stay internal.
  const canViewAttendanceHealth = ["admin", "super_admin", "operations_manager", "chief_security_officer"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  // Map<site_id, {buckets, total_shifts, total_missed, miss_rate}> — populated
  // only when the caller is entitled to view attendance health.
  const [attendanceHealth, setAttendanceHealth] = useState({});
  // Map<site_id, {incidents, maintenance, dar, tours}> — rolling 7-day counts
  // for the compact volume badge on each row.
  const [reportVolumes, setReportVolumes] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySite);
  const [view, setView] = useState("grid"); // "grid" | "list"
  const [clientFilter, setClientFilter] = useState([]);
  const [siteFilter, setSiteFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(["active"]);
  // Phase 2 — hide temp sites in the Sites list by default. Users can
  // toggle `includeTemp` back on to see them.
  const [includeTemp, setIncludeTemp] = useState(false);
  const [guardsModalSite, setGuardsModalSite] = useState(null);
  const [siteGuards, setSiteGuards] = useState([]);
  const [postOrdersSite, setPostOrdersSite] = useState(null);
  const [tourStopsSite, setTourStopsSite] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [geocodingAll, setGeocodingAll] = useState(false);
  const [geocodeAllResult, setGeocodeAllResult] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null); // { id, name }
  const [deactivateStatus, setDeactivateStatus] = useState("paused");
  // Agency-wide default bill rate ($/hr). Falls back to $28 constant if
  // the agency has never set it (also the backend's ultimate fallback).
  const [agencyDefaultRate, setAgencyDefaultRate] = useState(28);
  // Ad-hoc requesters (temp job walk-ins) — loaded once for the site
  // form's requester picker. Only fetched for users with the permission.
  const canViewAdhoc = usePermission("adhoc_requesters:view");
  const [adhocRequesters, setAdhocRequesters] = useState([]);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([api.get("/sites"), api.get("/clients")]);
    setItems(s.data); setClients(c.data);
    // Ad-hoc requesters — silently ignored when the user lacks view perm.
    if (canViewAdhoc) {
      try {
        const r = await api.get("/adhoc-requesters", { params: { status: "active" } });
        setAdhocRequesters(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        // Non-fatal: the site form gracefully falls back to client-only mode
        // when adhoc requesters can't be fetched. Log in dev so a 500 on
        // this endpoint doesn't disappear silently, but ship no toast — the
        // user's flow is unaffected.
        if (process.env.NODE_ENV === "development") console.debug("Sites: adhoc-requesters fetch skipped:", err?.response?.status);
      }
    }
    // Attendance-health sparklines — admin/OM/CSO only. Silently no-op for
    // client / site users so the network tab stays clean.
    if (canViewAttendanceHealth) {
      try {
        const h = await api.get("/sites/attendance-health?weeks=4");
        setAttendanceHealth(h.data?.sites || {});
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.debug("Sites: attendance-health fetch skipped:", err?.response?.status);
      }
    }
    // Report volumes — everyone sees their own scoped counts. Cheap, so no
    // gate; failures are non-fatal (badge just renders as dashes).
    try {
      const v = await api.get("/sites/report-volumes");
      setReportVolumes(v.data || {});
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("Sites: report-volumes fetch skipped:", err?.response?.status);
    }
    try {
      const u = await api.get("/users");
      const all = u.data || [];
      setSupervisors(all.filter((x) => ["supervisor", "admin"].includes(x.role)));
      setPortalUsers(all.filter((x) => ["client", "client_admin", "regional_manager", "site_manager"].includes(x.role)));
    } catch (err) {
      // /users is admin-scope only — supervisors/portal users list stays empty for non-admins.
      if (process.env.NODE_ENV === "development") console.debug("Sites: /users fetch skipped:", err?.response?.status);
    }
    // Agency-wide default bill rate for the "effective rate" column.
    // Endpoint is admin-only; non-admins silently keep the $28 fallback.
    try {
      const ops = await api.get("/agency/operational-settings");
      const r = Number(ops.data?.default_bill_rate_per_hour);
      if (Number.isFinite(r) && r > 0) setAgencyDefaultRate(r);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("Sites: operational-settings fetch skipped:", err?.response?.status);
    }
  }, [canViewAttendanceHealth, canViewAdhoc]);
  useEffect(() => { load(); }, [load]);

  // Deep-link: /sites?site=<id> auto-opens the edit drawer for that site
  // once the roster is loaded. Used by the Site Scorecard "Go to site
  // page" footer so admins can jump straight into a site's file.
  const [searchParams, setSearchParams] = useSearchParams();

  // iter242 hotfix — declared BEFORE the deep-link effect that captures it.
  // Otherwise React runs the render pass, hits the effect closure, and blows
  // up with "Cannot access 'onEdit' before initialization" (const TDZ).
  const onEdit = useCallback((s) => {
    setEditing(s);
    setForm({
      ...emptySite, ...s,
      street_address: s.street_address || "",
      city: s.city || "",
      state: s.state || "",
      zip_code: s.zip_code || "",
      lat: s.lat ?? "", lng: s.lng ?? "",
      timezone: s.timezone || "",
      deactivation_date: s.deactivation_date || "",
      post_orders_text: s.post_orders_text || "",
      bill_rate_per_hour: s.bill_rate_per_hour ?? "",
    });
    setOpen(true);
  }, []);

  useEffect(() => {
    const focus = searchParams.get("site");
    if (!focus || !items.length) return;
    const target = items.find((s) => s.id === focus);
    if (target) {
      onEdit(target);
      const next = new URLSearchParams(searchParams);
      next.delete("site");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams, onEdit]);

  // ── Bidirectional cross-narrowing ─────────────────────────────────────
  // Each filter dimension narrows the options for the OTHER dimensions.
  // (client → sites/cities/states) AND (site → clients/cities/states),
  // (city → clients/sites/states), (state → clients/sites/cities).
  const scopedByOthers = useCallback((exclude) => {
    const cf = exclude === "client" ? null : new Set(clientFilter);
    const sf = exclude === "site" ? null : new Set(siteFilter);
    const ctf = exclude === "city" ? null : new Set(cityFilter);
    const stf = exclude === "state" ? null : new Set(stateFilter);
    return items.filter((s) => {
      if (cf && cf.size && !cf.has(s.client_id)) return false;
      if (sf && sf.size && !sf.has(s.id)) return false;
      if (ctf && ctf.size && !ctf.has((s.city || "").trim())) return false;
      if (stf && stf.size && !stf.has((s.state || "").trim())) return false;
      return true;
    });
  }, [items, clientFilter, siteFilter, cityFilter, stateFilter]);

  // For the FILTER bar — cross-filters keep the "filter by client"
  // dropdown scoped to clients that own at least one currently-visible
  // site. Meaningful only when other filters (city/state/site) are active.
  // iter297 — Fix "only one client shown in Sites filter" bug on
  // production. The narrow-by-current-sites logic used to run
  // unconditionally: any client whose sites lacked a proper `client_id`
  // (linked by `client_name` string only) got dropped from the dropdown
  // because `.filter(Boolean)` erased them. Now we only narrow when the
  // OTHER filters (city / state / site) are actually active — otherwise
  // we surface every active client so ops can filter to any client
  // even if data-linkage on their sites is stale. When narrowing, we
  // ALSO match by `client_name` as a fallback so a client with sites
  // linked purely by legacy name still stays in the dropdown.
  const clientFilterOptions = useMemo(() => {
    const hasOtherFilters =
      siteFilter.length > 0 || cityFilter.length > 0 || stateFilter.length > 0;
    if (!hasOtherFilters) {
      // No other filter active → show every active client.
      return clients
        .filter((c) => (c.status || "active") === "active")
        .map((c) => ({ value: c.id, label: c.company_name, sub: formatAddress(c) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    const scoped = scopedByOthers("client");
    const allowedIds = new Set(scoped.map((s) => s.client_id).filter(Boolean));
    const allowedNames = new Set(scoped.map((s) => (s.client_name || "").trim()).filter(Boolean));
    return clients
      .filter((c) => allowedIds.has(c.id) || allowedNames.has((c.company_name || "").trim()))
      .map((c) => ({ value: c.id, label: c.company_name, sub: formatAddress(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clients, scopedByOthers, siteFilter, cityFilter, stateFilter]);

  // For the "New Site" MODAL — every ACTIVE client, regardless of whether
  // they already own sites. A brand-new (active) client has zero sites, so
  // filtering by "clients that already have sites" (as clientFilterOptions
  // does) would hide them from the picker and break the onboarding flow.
  // Inactive / on-hold / churned clients are excluded so admins can't
  // accidentally file a fresh contract under a dormant relationship.
  const clientOptions = useMemo(
    () => clients
      .filter((c) => (c.status || "active") === "active")
      .map((c) => ({ value: c.id, label: c.company_name, sub: formatAddress(c) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [clients],
  );
  const hiddenInactiveClientCount = useMemo(
    () => clients.filter((c) => (c.status || "active") !== "active").length,
    [clients],
  );

  const siteOptions = useMemo(() => scopedByOthers("site")
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [scopedByOthers]);

  // Self-heal: drop selections that fell out of the visible option pools.
  useEffect(() => {
    const allowed = new Set(siteOptions.map((o) => o.value));
    if (siteFilter.some((id) => !allowed.has(id))) setSiteFilter(siteFilter.filter((id) => allowed.has(id)));
  }, [siteOptions, siteFilter]);
  useEffect(() => {
    const allowed = new Set(clientFilterOptions.map((o) => o.value));
    if (clientFilter.some((id) => !allowed.has(id))) setClientFilter(clientFilter.filter((id) => allowed.has(id)));
  }, [clientFilterOptions, clientFilter]);

  const missingCoordsCount = useMemo(
    () => items.filter((s) => s.lat == null || s.lng == null).length,
    [items],
  );

  const runGeocodeAll = async () => {
    if (geocodingAll) return;
    if (missingCoordsCount === 0) {
      toast.success("All sites already have coordinates.");
      return;
    }
    if (!window.confirm(`Backfill lat/lng for ${missingCoordsCount} site(s) missing coordinates? This calls US Census + OpenStreetMap and may take a moment.`)) {
      return;
    }
    setGeocodingAll(true);
    setGeocodeAllResult(null);
    try {
      const r = await api.post("/sites/geocode-all");
      setGeocodeAllResult(r.data);
      const okN = r.data?.updated?.length || 0;
      const failN = r.data?.failed?.length || 0;
      const skipN = r.data?.skipped_no_address?.length || 0;
      if (okN > 0) toast.success(`Geocoded ${okN} site${okN === 1 ? "" : "s"}${failN ? ` — ${failN} could not be located` : ""}${skipN ? ` (${skipN} had no address)` : ""}`);
      else if (failN > 0) toast.error(`No sites geocoded — ${failN} could not be located`);
      else toast.info("Nothing to geocode.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Geocode-all failed"));
    } finally {
      setGeocodingAll(false);
    }
  };

  // iter298 — One-click relink for legacy sites whose `client_id` is
  // missing (they only carry a `client_name` string). Uses the new
  // backend endpoint to bulk-fix the FK; surfaces the unmatched list so
  // admins can decide whether to create the missing client or edit the
  // site manually.
  const [relinking, setRelinking] = useState(false);
  const legacyClientlessCount = useMemo(
    () => items.filter((s) => !s.client_id && (s.client_name || "").trim()).length,
    [items],
  );
  const onRelinkLegacy = async () => {
    if (!window.confirm(`Relink ${legacyClientlessCount} legacy site${legacyClientlessCount === 1 ? "" : "s"} to their clients by matching on Company Name?`)) return;
    setRelinking(true);
    try {
      const r = await api.post("/sites/relink-legacy-clients");
      const { relinked = 0, unmatched = [] } = r.data || {};
      if (relinked > 0) {
        toast.success(`Relinked ${relinked} site${relinked === 1 ? "" : "s"}${unmatched.length ? ` — ${unmatched.length} still need a client` : ""}`);
      } else if (unmatched.length > 0) {
        toast.warning(`No matches. ${unmatched.length} site${unmatched.length === 1 ? "" : "s"} had client names with no matching Client record.`);
      } else {
        toast.info("Nothing to relink.");
      }
      // Surface unmatched details inline so the admin sees exactly which
      // legacy names had no client — actionable next step is "create
      // that client" or "edit the site".
      if (unmatched.length) {
        const preview = unmatched.slice(0, 8).map((u) => `• ${u.site_name} → “${u.client_name}”`).join("\n");
        const more = unmatched.length > 8 ? `\n… +${unmatched.length - 8} more` : "";
        window.alert(`Unmatched sites (create these clients or edit the site to pick an existing one):\n\n${preview}${more}`);
      }
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Relink failed"));
    } finally {
      setRelinking(false);
    }
  };

  // Bidirectional city/state options — narrow based on other selected filters.
  const locOptions = useMemo(() => ({
    cities: distinctLocations(scopedByOthers("city")).cities,
    states: distinctLocations(scopedByOthers("state")).states,
  }), [scopedByOthers]);
  useEffect(() => {
    const allowed = new Set(locOptions.cities.map((o) => o.value));
    if (cityFilter.some((v) => !allowed.has(v))) setCityFilter(cityFilter.filter((v) => allowed.has(v)));
  }, [locOptions.cities, cityFilter]);
  useEffect(() => {
    const allowed = new Set(locOptions.states.map((o) => o.value));
    if (stateFilter.some((v) => !allowed.has(v))) setStateFilter(stateFilter.filter((v) => allowed.has(v)));
  }, [locOptions.states, stateFilter]);

  const visibleItems = useMemo(() => {
    const cf = new Set(clientFilter);
    const sf = new Set(siteFilter);
    const ctf = new Set(cityFilter);
    const stf = new Set(stateFilter);
    const stsf = new Set(statusFilter);
    return items.filter((s) => {
      if (cf.size && !cf.has(s.client_id)) return false;
      if (sf.size && !sf.has(s.id)) return false;
      if (ctf.size && !ctf.has((s.city || "").trim())) return false;
      if (stf.size && !stf.has((s.state || "").trim())) return false;
      if (stsf.size && !stsf.has(s.status || "active")) return false;
      if (!includeTemp && s.is_temporary) return false;
      return true;
    });
  }, [items, clientFilter, siteFilter, cityFilter, stateFilter, statusFilter, includeTemp]);

  const SITE_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "ended", label: "Ended" },
    { value: "paused", label: "Paused" },
  ];

  const onSave = async () => {
    if (!form.name?.trim()) { toast.error("Site name is required"); return; }
    if (!form.client_id) { toast.error("Pick an existing client (create one from the Clients tab)"); return; }
    if (!form.address?.trim() && !form.street_address?.trim()) {
      toast.error("Provide an address (street + city + state + zip, or the legacy single line)"); return;
    }
    const synthesized = !form.address?.trim()
      ? [form.street_address, form.city, form.state, form.zip_code].filter(Boolean).join(", ")
      : form.address.trim();
    const payload = {
      ...form,
      address: synthesized,
      guards_required: Number(form.guards_required) || 1,
      contract_value: Number(form.contract_value) || 0,
      bill_rate_per_hour:
        form.bill_rate_per_hour === "" || form.bill_rate_per_hour === null
          ? null
          : Number(form.bill_rate_per_hour),
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      geofence_radius_m: Number(form.geofence_radius_m) || 200,
      deactivation_date: form.deactivation_date || null,
      post_orders_text: form.post_orders_text || null,
    };
    try {
      if (editing) await api.put(`/sites/${editing.id}`, payload);
      else await api.post("/sites", payload);
      toast.success(editing ? "Site updated" : "Site added");
      setOpen(false); setEditing(null); setForm(emptySite);
      load();
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const onDelete = (id) => {
    const site = items.find((s) => s.id === id);
    if (!site) return;
    setDeactivateStatus("paused");
    setDeactivateTarget({ id, name: site.name || "site" });
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    const { id, name } = deactivateTarget;
    const status = deactivateStatus;
    setDeactivateTarget(null);
    try {
      const r = await api.delete(`/sites/${id}?status=${status}`);
      const n = r.data?.cancelled_shifts || 0;
      toast.success(`"${name}" → ${status}${n ? ` · ${n} future shift${n === 1 ? "" : "s"} cancelled` : ""}`);
      load();
    } catch (e) { toast.error(errorMessage(e, "Deactivation failed")); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/sites/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "sites.csv"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e, "Export failed")); }
  };

  const openNew = async () => {
    // Refetch clients + adhoc requesters so newly-created records show up
    // without a manual page reload.
    let latest = clients;
    try {
      const [rc, ra] = await Promise.all([
        api.get("/clients"),
        canViewAdhoc
          ? api.get("/adhoc-requesters", { params: { status: "active" } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      latest = rc.data || [];
      setClients(latest);
      setAdhocRequesters(Array.isArray(ra.data) ? ra.data : []);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("Sites: refetch clients skipped:", err?.response?.status);
    }
    // Sites without any client OR any adhoc requester have nothing to link to.
    const activeClientCount = latest.filter((c) => (c.status || "active") === "active").length;
    if (activeClientCount === 0 && (!canViewAdhoc || adhocRequesters.length === 0)) {
      toast.error(
        canViewAdhoc
          ? "Add a client OR an ad-hoc requester before creating a site"
          : "Add a client first from the Clients tab"
      );
      return;
    }
    setEditing(null); setForm(emptySite); setOpen(true);
  };

  const openGuards = async (s) => {
    setGuardsModalSite(s);
    setSiteGuards([]);
    try {
      const r = await api.get(`/sites/${s.id}/guards`);
      setSiteGuards(r.data);
    } catch { toast.error("Failed to load "); }
  };

  const uploadPostOrdersDoc = async (e, siteId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/sites/${siteId}/post-orders/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Post orders document uploaded");
      load();
      if (postOrdersSite?.id === siteId) {
        const updated = (await api.get("/sites")).data.find((x) => x.id === siteId);
        setPostOrdersSite(updated);
      }
    } catch (e) { toast.error(errorMessage(e, "Upload failed")); }
    e.target.value = "";
  };

  const tone = (s) => ({ active: "green", paused: "amber", ended: "default" }[s] || "default");
  const isDeactivated = (s) => s.deactivation_date && new Date(s.deactivation_date) <= new Date();
  const mapUrl = (s) => s.lat != null && s.lng != null
    ? `https://www.openstreetmap.org/?mlat=${s.lat}&mlon=${s.lng}#map=17/${s.lat}/${s.lng}`
    : null;

  return (
    <div>
      <PageHeader
        title="Sites"
        subtitle="Client locations under contract. Click any card to view post orders, assigned , and geofence."
        action={
          <div className="flex gap-2">
            <div className="inline-flex border border-[#27272A]" data-testid="view-toggle">
              <button data-testid="view-grid" onClick={() => setView("grid")}
                className={`px-3 py-2 text-xs uppercase tracking-widest ${view === "grid" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}>
                <Grid3x3 className="size-3.5" />
              </button>
              <button data-testid="view-list" onClick={() => setView("list")}
                className={`px-3 py-2 text-xs uppercase tracking-widest ${view === "list" ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"}`}>
                <ListIcon className="size-3.5" />
              </button>
            </div>
            {isAdmin && (
              <GhostButton
                data-testid="geocode-all-button"
                onClick={runGeocodeAll}
                disabled={geocodingAll}
                title={missingCoordsCount === 0 ? "All sites already have coordinates" : `Backfill lat/lng for ${missingCoordsCount} site(s) missing coordinates`}
              >
                <Navigation className="size-4" />
                {geocodingAll ? "Geocoding…" : `Geocode all${missingCoordsCount ? ` (${missingCoordsCount})` : ""}`}
              </GhostButton>
            )}
            {/* iter298 — Legacy-client relink admin action. Only surfaces
                when there ARE legacy sites (so admins with clean data
                never see it). Auto-matches by company_name; unmatched
                cases surface in an alert for manual follow-up. */}
            {isAdmin && legacyClientlessCount > 0 && (
              <GhostButton
                data-testid="relink-legacy-clients-button"
                onClick={onRelinkLegacy}
                disabled={relinking}
                title={`Auto-fix ${legacyClientlessCount} site(s) that reference a client by name only — matches on Company Name and back-fills client_id.`}
              >
                <Link2 className="size-4" />
                {relinking ? "Relinking…" : `Relink to Clients (${legacyClientlessCount})`}
              </GhostButton>
            )}
            <GhostButton data-testid="sites-export-csv" onClick={exportCsv} title="Download sites as CSV">
              <Download className="size-4" /> CSV
            </GhostButton>
            <ActionButton data-testid="add-site-button" onClick={openNew} style={!canCreateSite ? { display: "none" } : undefined}>
              <Plus className="size-4" /> New Site
            </ActionButton>
          </div>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap items-end gap-3" data-testid="sites-filters">
          <div>
            <div className="label-mute mb-1">Client</div>
            <MultiSelectDropdown testId="filter-client" label="All clients" options={clientFilterOptions}
              selected={clientFilter} onChange={setClientFilter} searchable triggerClassName="min-w-[200px]" emptyHint="No clients match" />
          </div>
          <div>
            <div className="label-mute mb-1">Site {clientFilter.length > 0 && <span className="text-[#0A84FF]">· filtered by client</span>}</div>
            <MultiSelectDropdown testId="filter-site" label="All sites" options={siteOptions}
              selected={siteFilter} onChange={setSiteFilter} searchable triggerClassName="min-w-[200px]" emptyHint="No sites match" />
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> City</div>
            <MultiSelectDropdown testId="filter-city" label="All cities" options={locOptions.cities}
              selected={cityFilter} onChange={setCityFilter} searchable triggerClassName="min-w-[170px]" emptyHint="No cities yet" />
          </div>
          <div>
            <div className="label-mute mb-1 flex items-center gap-1"><MapPin className="size-3" /> State</div>
            <MultiSelectDropdown testId="filter-state" label="All states" options={locOptions.states}
              selected={stateFilter} onChange={setStateFilter} searchable triggerClassName="min-w-[140px]" emptyHint="No states yet" />
          </div>
          <div>
            <div className="label-mute mb-1">Status</div>
            <MultiSelectDropdown testId="filter-status" label="All statuses" options={SITE_STATUS_OPTIONS}
              selected={statusFilter} onChange={setStatusFilter} triggerClassName="min-w-[140px]" />
          </div>
          {/* Phase 2 — temp job opt-in filter */}
          <div className="pb-2">
            <div className="label-mute mb-1">Temp jobs</div>
            <label className="flex items-center gap-2 h-[38px] px-3 border border-[#27272A] bg-[#0A0A0A] cursor-pointer text-xs uppercase tracking-widest text-zinc-400 hover:text-zinc-200 transition-colors">
              <input
                type="checkbox"
                checked={includeTemp}
                onChange={(e) => setIncludeTemp(e.target.checked)}
                className="accent-[#FF9F0A] size-3.5"
                data-testid="sites-include-temp"
              />
              {includeTemp ? "Included" : "Hidden"}
            </label>
          </div>
          {(clientFilter.length || siteFilter.length || cityFilter.length || stateFilter.length || statusFilter.length) > 0 && (
            <button data-testid="clear-filters" onClick={() => { setClientFilter([]); setSiteFilter([]); setCityFilter([]); setStateFilter([]); setStatusFilter([]); }}
              className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] pb-2">
              Clear
            </button>
          )}
          <div className="ml-auto text-xs font-mono text-zinc-500 pb-2">
            {visibleItems.length} / {items.length} sites · {clients.length} clients
          </div>
        </div>

        {view === "grid" && (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="sites-grid">
            {visibleItems.map((s) => (
              <div key={s.id} data-testid={`site-card-${s.id}`} className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="label-mute flex items-center gap-1"><Building2 className="size-3" />{s.client_name}</div>
                    <h3 className="font-display text-2xl uppercase font-bold tracking-tight mt-1">{s.name}</h3>
                    <div className="text-sm text-zinc-400 mt-2">{s.address}</div>
                    {s.lat != null && s.lng != null && (
                      <a href={mapUrl(s)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-[#34C759] mt-2 font-mono">
                        <MapPin className="size-3 text-[#34C759]" />
                        {Number(s.lat).toFixed(5)}, {Number(s.lng).toFixed(5)} · {s.geofence_radius_m || 200}m fence <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PillBadge tone={isDeactivated(s) ? "red" : tone(s.status)}>
                      {isDeactivated(s) ? "deactivated" : s.status}
                    </PillBadge>
                    {s.deactivation_date && !isDeactivated(s) && (
                      <span className="text-[10px] font-mono text-[#FF9F0A]">deact {s.deactivation_date}</span>
                    )}
                    {!isDeactivated(s) && (!Array.isArray(s.supervisor_ids) || s.supervisor_ids.length === 0) && (
                      <span
                        data-testid={`no-supervisor-badge-${s.id}`}
                        title="No supervisor assigned"
                        className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FF9F0A]/10 border border-[#FF9F0A]/40 text-[#FF9F0A]"
                      >
                        ⚠ No supervisor
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5 pt-4 border-t border-[#27272A]">
                  <div><div className="label-mute">Officers Req.</div><div className="font-mono font-bold text-lg mt-1">{s.guards_required}</div></div>
                  <div><div className="label-mute">Contract</div><div className="font-mono font-bold text-lg mt-1">${Number(s.contract_value).toLocaleString()}</div></div>
                  <div data-testid={`site-bill-rate-cell-${s.id}`}>
                    <div className="label-mute">Bill Rate</div>
                    {(() => {
                      const { rate, source } = effectiveBillRate(s, agencyDefaultRate);
                      return (
                        <div className="mt-1 flex items-baseline gap-1.5">
                          <span className={`font-mono font-bold text-lg ${source === "site" ? "text-[#FF3B30]" : "text-zinc-300"}`}>
                            ${rate.toFixed(2)}
                          </span>
                          <span className="text-[9px] uppercase tracking-widest text-zinc-500" title={source === "site" ? "Per-site override" : `Inherited from agency default ($${agencyDefaultRate}/hr)`}>
                            {source === "site" ? "override" : "default"}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div><div className="label-mute">Contact</div><div className="font-mono text-sm mt-1 truncate">{s.contact_phone || "—"}</div></div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 justify-end">
                  <GhostButton data-testid={`post-orders-${s.id}`} onClick={() => setPostOrdersSite(s)}><ScrollText className="size-3.5" /> Post Orders</GhostButton>
                  <GhostButton data-testid={`tour-stops-${s.id}`} onClick={() => setTourStopsSite(s)}><Route className="size-3.5" /> Tour Stops</GhostButton>
                  <GhostButton data-testid={`view-guards-${s.id}`} onClick={() => openGuards(s)}><UsersIcon className="size-3.5" /> Officers</GhostButton>
                  <GhostButton data-testid={`site-edit-${s.id}`} onClick={() => onEdit(s)}><Pencil className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`site-trash-${s.id}`} onClick={() => onDelete(s.id)}><Trash2 className="size-3.5" /></GhostButton>
                </div>
              </div>
            ))}
            {visibleItems.length === 0 && <div className="border border-[#27272A] p-12 text-center text-zinc-500 col-span-full scanlines">No sites match filters.</div>}
          </div>
        )}

        {view === "list" && (
          <div className="md:hidden grid gap-4" data-testid="sites-grid-mobile-fallback">
            {visibleItems.map((s) => (
              <div key={s.id} data-testid={`site-card-mobile-${s.id}`} className="border border-[#27272A] bg-[#121212] p-4 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="label-mute flex items-center gap-1"><Building2 className="size-3" /><span className="truncate">{s.client_name}</span></div>
                    <h3 className="font-display text-lg uppercase font-bold tracking-tight mt-1 break-words">{s.name}</h3>
                    <div className="text-xs text-zinc-400 mt-1 break-words">{s.address}</div>
                  </div>
                  <PillBadge tone={isDeactivated(s) ? "red" : tone(s.status)}>
                    {isDeactivated(s) ? "deactivated" : s.status}
                  </PillBadge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#27272A]">
                  <div><div className="label-mute">Officers</div><div className="font-mono font-bold text-sm mt-0.5">{s.guards_required}</div></div>
                  <div>
                    <div className="label-mute">Bill/hr</div>
                    {(() => {
                      const { rate, source } = effectiveBillRate(s, agencyDefaultRate);
                      return (
                        <div className={`font-mono font-bold text-sm mt-0.5 ${source === "site" ? "text-[#FF3B30]" : "text-zinc-300"}`}>
                          ${rate.toFixed(2)}
                        </div>
                      );
                    })()}
                  </div>
                  <div><div className="label-mute">Reports 7d</div><ReportVolumeBadge siteId={s.id} /></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 justify-end">
                  <GhostButton data-testid={`post-orders-mobile-${s.id}`} onClick={() => setPostOrdersSite(s)}><ScrollText className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`tour-stops-mobile-${s.id}`} onClick={() => setTourStopsSite(s)}><Route className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`view-guards-mobile-${s.id}`} onClick={() => openGuards(s)}><UsersIcon className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`site-edit-mobile-${s.id}`} onClick={() => onEdit(s)}><Pencil className="size-3.5" /></GhostButton>
                  <GhostButton data-testid={`site-trash-mobile-${s.id}`} onClick={() => onDelete(s.id)}><Trash2 className="size-3.5" /></GhostButton>
                </div>
              </div>
            ))}
            {visibleItems.length === 0 && <div className="border border-[#27272A] p-8 text-center text-zinc-500 scanlines">No sites match filters.</div>}
          </div>
        )}

        {view === "list" && (
          <div className="hidden md:block border border-[#27272A] bg-[#121212] overflow-x-auto">
            <table className="w-full text-sm" data-testid="sites-table">
              <thead className="border-b border-[#27272A]">
                <tr className="label-mute">
                  <th className="px-2 py-3 w-[40px] text-center">#</th>
                  <th className="px-4 py-3 text-left">Site</th>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Address</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Officers</th>
                  <th className="px-4 py-3 text-left">Contract</th>
                  <th className="px-4 py-3 text-left" data-testid="sites-col-bill-rate" title="Site override in red; agency default when inherited">Bill Rate</th>
                  <th className="px-4 py-3 text-left" data-testid="sites-col-report-volume" title="Report activity in the last 7 days">Reports (7d)</th>
                  {canViewAttendanceHealth && (
                    <th className="px-4 py-3 text-left" data-testid="sites-col-health">Attendance (4w)</th>
                  )}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {visibleItems.map((s, idx) => (
                  <tr key={s.id} className="hover:bg-[#1c1c1e]">
                    <td data-testid={`row-num-${s.id}`} className="px-2 py-3 text-center font-mono text-zinc-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-display font-bold uppercase tracking-tight">{s.name}</td>
                    <td className="px-4 py-3 text-zinc-400">{s.client_name}</td>
                    <td className="px-4 py-3 text-zinc-400 truncate max-w-[260px]">{formatAddress(s)}</td>
                    <td className="px-4 py-3">
                      <PillBadge tone={isDeactivated(s) ? "red" : tone(s.status)}>
                        {isDeactivated(s) ? "deactivated" : s.status}
                      </PillBadge>
                    </td>
                    <td className="px-4 py-3 font-mono">{s.guards_required}</td>
                    <td className="px-4 py-3 font-mono">${Number(s.contract_value).toLocaleString()}</td>
                    <td className="px-4 py-3" data-testid={`site-bill-rate-row-${s.id}`}>
                      {(() => {
                        const { rate, source } = effectiveBillRate(s, agencyDefaultRate);
                        return (
                          <span className="inline-flex items-baseline gap-1.5">
                            <span className={`font-mono ${source === "site" ? "text-[#FF3B30] font-bold" : "text-zinc-300"}`}>
                              ${rate.toFixed(2)}
                            </span>
                            <span
                              className="text-[9px] uppercase tracking-widest text-zinc-500"
                              title={source === "site" ? "Per-site override" : `Inherited from agency default ($${agencyDefaultRate}/hr)`}
                            >
                              {source === "site" ? "override" : "default"}
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <ReportVolumeBadge volumes={reportVolumes[s.id]} testId={`site-report-volume-${s.id}`} />
                    </td>
                    {canViewAttendanceHealth && (
                      <td className="px-4 py-3" data-testid={`site-health-${s.id}`}>
                        <AttendanceHealthSparkline
                          buckets={attendanceHealth[s.id]?.buckets}
                          miss_rate={attendanceHealth[s.id]?.miss_rate}
                          href={attendanceHealth[s.id]?.total_missed > 0 ? `/attendance?site=${s.id}&missed=1` : undefined}
                          testId={`site-health-sparkline-${s.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <GhostButton onClick={() => setPostOrdersSite(s)}><ScrollText className="size-3.5" /></GhostButton>
                        <GhostButton data-testid={`tour-stops-list-${s.id}`} onClick={() => setTourStopsSite(s)}><Route className="size-3.5" /></GhostButton>
                        <GhostButton onClick={() => openGuards(s)}><UsersIcon className="size-3.5" /></GhostButton>
                        <GhostButton data-testid={`site-edit-${s.id}`} onClick={() => onEdit(s)}><Pencil className="size-3.5" /></GhostButton>
                        <GhostButton data-testid={`site-trash-${s.id}`} onClick={() => onDelete(s.id)}><Trash2 className="size-3.5" /></GhostButton>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleItems.length === 0 && <tr><td colSpan={canViewAttendanceHealth ? 10 : 9} className="text-center py-16 text-zinc-500 scanlines">No sites match filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── New / Edit Site modal ──────────────────────────────────── */}
      {open && (
        <Modal title={editing ? "Edit Site" : "Add Site"} onClose={() => setOpen(false)} testId="site-form-modal">
          <div className="space-y-5">
            {editing && (
              <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
                <SiteRecentActivity siteId={editing.id} />
              </div>
            )}

            {/* ── Temp job toggle ───────────────────────────────── */}
            <TempJobSection form={form} setForm={setForm} adhocRequesters={adhocRequesters} />

            {/* ── Client / requester picker ─────────────────────── */}
            {!form.adhoc_requester_id && (
            <div className="border border-[#27272A] bg-[#0A0A0A] p-4">
              <div className="label-mute mb-2 flex items-center gap-2"><Building2 className="size-3" /> Client</div>
              <FormField label={form.is_temporary ? "Existing Client (temp under this client)" : "Existing Client *"}>
                <SingleSelectDropdown
                  testId="site-client-select"
                  label="Choose a client"
                  options={clientOptions}
                  value={form.client_id || ""}
                  onChange={(v) => setForm({ ...form, client_id: v, adhoc_requester_id: "" })}
                  searchable
                  triggerClassName="w-full"
                  emptyHint="No clients match"
                />
              </FormField>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-2" data-testid="site-client-picker-hint">
                Add or edit clients from the <span className="text-[#FF3B30]">Clients</span> tab.
                {hiddenInactiveClientCount > 0 && (
                  <span className="block text-[#FF9F0A] mt-1" data-testid="site-client-inactive-hidden">
                    {hiddenInactiveClientCount} inactive client{hiddenInactiveClientCount === 1 ? "" : "s"} hidden — reactivate them to assign new sites.
                  </span>
                )}
              </div>
              {form.client_id && (() => {
                const chosen = clients.find((c) => c.id === form.client_id);
                const portfolios = (chosen?.portfolios || []);
                if (portfolios.length === 0) return null;
                return (
                  <FormField label="Portfolio (optional)">
                    <select
                      data-testid="site-portfolio-select"
                      className={inputClass}
                      value={form.portfolio_id || ""}
                      onChange={(e) => setForm({ ...form, portfolio_id: e.target.value || null })}
                    >
                      <option value="">— unassigned —</option>
                      {portfolios.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </FormField>
                );
              })()}
              {/* Per-site portal-visibility prompt — only shown when
                  is_temporary=true AND under a real client (Flavour ②).
                  Design §3 A.c — ops explicitly answers per temp site. */}
              {form.is_temporary && form.client_id && (
                <FormField label="Show this temp job in the client's portal?">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#FF3B30] size-4"
                      checked={!!form.portal_visible}
                      onChange={(e) => setForm({ ...form, portal_visible: e.target.checked })}
                      data-testid="site-portal-visible"
                    />
                    <span className="text-xs text-zinc-400">
                      {form.portal_visible ? "Yes — visible in client portal" : "No — internal only (default for temp)"}
                    </span>
                  </label>
                </FormField>
              )}
            </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Site Name *"><input data-testid="site-name-input" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
              <FormField label="Status">
                <select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option><option value="paused">Paused</option><option value="ended">Ended</option>
                </select>
              </FormField>
              <div className="col-span-2 border-t border-[#27272A] pt-3">
                <div className="label-mute mb-2 flex items-center gap-1.5"><MapPin className="size-3" /> Address</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Street (Number + Name)">
                    <input data-testid="site-street" className={inputClass} placeholder="123 Main St"
                      value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} />
                  </FormField>
                  <FormField label="City">
                    <input data-testid="site-city" className={inputClass} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </FormField>
                  <FormField label="State">
                    <input data-testid="site-state" className={inputClass} placeholder="NY" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                  </FormField>
                  <FormField label="Zip / Postal Code">
                    <input data-testid="site-zip" className={inputClass} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
                  </FormField>
                </div>
                {(editing && !form.street_address && !form.city && form.address) && (
                  <div className="mt-3">
                    <FormField label="Legacy address (one-line — re-enter the parts above to migrate)">
                      <input data-testid="site-address-input" className={inputClass} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                    </FormField>
                  </div>
                )}
              </div>
              <FormField label="Contact Person"><input className={inputClass} value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></FormField>
              <FormField label="Contact Phone"><input className={inputClass} value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></FormField>
              <FormField label="Officers Required"><input type="number" min="0" className={inputClass} value={form.guards_required} onChange={(e) => setForm({ ...form, guards_required: e.target.value })} /></FormField>
              <FormField label="Contract Value (USD/mo)"><input type="number" min="0" className={inputClass} value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} /></FormField>
              <FormField label="Bill Rate ($/hr)">
                <input
                  data-testid="site-bill-rate-input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Uses agency default"
                  className={inputClass}
                  value={form.bill_rate_per_hour}
                  onChange={(e) => setForm({ ...form, bill_rate_per_hour: e.target.value })}
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Leave blank to inherit the agency default. Powers the Metrics tab&apos;s unrealized-revenue calc.
                </div>
              </FormField>
              <FormField label="Latitude">
                <div className="flex gap-2">
                  <input type="number" step="0.00001" className={inputClass} value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                  <button
                    type="button"
                    data-testid="geocode-from-address"
                    onClick={async () => {
                      const addr = (form.address?.trim() || [form.street_address, form.city, form.state, form.zip_code].filter(Boolean).join(", "));
                      if (!addr) { toast.error("Enter an address first"); return; }
                      try {
                        const r = await api.post("/geocode/preview", { address: addr });
                        setForm((f) => ({ ...f, lat: r.data.lat.toFixed(6), lng: r.data.lng.toFixed(6) }));
                        toast.success("Located from address");
                      } catch (e) {
                        toast.error(errorMessage(e, "Couldn't locate that address"));
                      }
                    }}
                    className="shrink-0 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 text-[10px] uppercase tracking-widest px-3 transition-colors"
                    title="Auto-fill latitude/longitude from the address above"
                  >
                    Locate
                  </button>
                </div>
              </FormField>
              <FormField label="Longitude"><input type="number" step="0.00001" className={inputClass} value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></FormField>
              <FormField label="Geofence Radius (m)"><input type="number" min="10" className={inputClass} value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} /></FormField>
              <FormField label="Timezone (IANA)">
                <div className="flex gap-2">
                  <input
                    data-testid="site-timezone-input"
                    className={inputClass}
                    placeholder="e.g. America/New_York — leave blank to auto-detect"
                    value={form.timezone || ""}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                    list="iana-tz-list"
                  />
                  <datalist id="iana-tz-list">
                    {[
                      "America/New_York","America/Chicago","America/Denver","America/Los_Angeles",
                      "America/Phoenix","America/Anchorage","America/Detroit","America/Boise",
                      "Pacific/Honolulu","America/Puerto_Rico","America/Toronto","America/Vancouver",
                      "Europe/London","Europe/Paris","Europe/Berlin","Asia/Tokyo","Asia/Karachi",
                    ].map((tz) => <option key={tz} value={tz} />)}
                  </datalist>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Auto-derived from lat/lng or ZIP on save when left blank. All timestamps for this site render in this zone.
                </div>
              </FormField>
              <div className="col-span-2">
                <div className="label-mute mb-1">Geofence Map (click to drop the pin)</div>
                <GeofenceMap
                  lat={form.lat === "" ? null : Number(form.lat)}
                  lng={form.lng === "" ? null : Number(form.lng)}
                  radius_m={Number(form.geofence_radius_m) || 200}
                  editable
                  onPick={(lat, lng) => setForm({ ...form, lat: lat.toFixed(6), lng: lng.toFixed(6) })}
                  height="280px"
                />
              </div>
              <FormField label="Deactivation Date (optional, future ok)">
                <input data-testid="site-deactivation-input" type="date" className={inputClass} value={form.deactivation_date || ""} onChange={(e) => setForm({ ...form, deactivation_date: e.target.value })} />
              </FormField>
              <div className="col-span-2">
                <FormField label="Assigned Supervisors (mobile app visibility)">
                  <MultiSelectDropdown
                    testId="site-supervisors"
                    label="No supervisors assigned"
                    options={supervisors.map((u) => ({ value: u.id, label: u.name || u.email, sub: u.email }))}
                    selected={form.supervisor_ids || []}
                    onChange={(v) => setForm({ ...form, supervisor_ids: v })}
                    searchable
                    triggerClassName="w-full"
                    emptyHint="No supervisor users yet"
                  />
                </FormField>
              </div>
              <div className="col-span-2 border-t border-[#27272A] pt-3">
                <div className="label-mute mb-2 flex items-center gap-1.5">Site Alert Notifications</div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    data-testid="site-tardy-notif-enabled"
                    type="checkbox"
                    className="mt-0.5 accent-[#FF3B30]"
                    checked={!!form.tardy_notifications_enabled}
                    onChange={(e) => setForm({ ...form, tardy_notifications_enabled: e.target.checked })}
                  />
                  <span className="text-sm text-zinc-300">
                    Activate tardy notifications
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Escalating emails at 15 / 30 / 45 / 60 / 90 / 120 min late.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer select-none mt-3">
                  <input
                    data-testid="site-geofence-notif-enabled"
                    type="checkbox"
                    className="mt-0.5 accent-[#FF3B30]"
                    checked={!!form.geofence_notifications_enabled}
                    onChange={(e) => setForm({ ...form, geofence_notifications_enabled: e.target.checked })}
                  />
                  <span className="text-sm text-zinc-300">
                    Activate geofence-breach notifications
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Mobile pings every 120s while on shift. Emails + breach report when the guard leaves the {form.geofence_radius_m || 200}m fence.
                    </span>
                  </span>
                </label>

                {/* Auto-scheduled status checks — high-risk sites only */}
                <div className="mt-4 border-t border-[#27272A] pt-3">
                  <FormField label="Auto status-check interval (minutes)">
                    <input
                      data-testid="site-status-check-interval"
                      type="number"
                      min="0"
                      step="5"
                      className={inputClass}
                      placeholder="Leave blank or 0 to disable"
                      value={form.status_check_interval_minutes ?? ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setForm({
                          ...form,
                          status_check_interval_minutes: raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0),
                        });
                      }}
                    />
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      When set,  on shift get a &ldquo;confirm you&rsquo;re OK&rdquo; prompt every N minutes. Missed acks lower the site&rsquo;s response health.
                    </div>
                  </FormField>
                </div>

                {/* Break policy — per-site overrides on top of the agency default */}
                <div className="mt-4 border-t border-[#27272A] pt-3 space-y-2">
                  <div className="label-mute">Break Policy</div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      data-testid="site-breaks-enabled"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      checked={form.breaks_enabled !== false}
                      onChange={(e) => setForm({ ...form, breaks_enabled: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      Allow  to take breaks at this site
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        Uncheck for short-post assignments where a break isn&rsquo;t operationally acceptable.
                      </span>
                    </span>
                  </label>

                  <label className={`flex items-start gap-2 select-none ${form.breaks_enabled === false ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                    <input
                      data-testid="site-breaks-all-unpaid"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      disabled={form.breaks_enabled === false}
                      checked={!!form.all_breaks_unpaid}
                      onChange={(e) => setForm({ ...form, all_breaks_unpaid: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      All breaks at this site are unpaid
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        Overrides the paid cutoff — every break is deducted regardless of duration.
                      </span>
                    </span>
                  </label>

                  <FormField label="Paid break cutoff (minutes)">
                    <input
                      data-testid="site-paid-break-max"
                      type="number"
                      min="0"
                      step="1"
                      className={inputClass}
                      placeholder="Leave blank to inherit agency default (15)"
                      disabled={form.breaks_enabled === false || !!form.all_breaks_unpaid}
                      value={form.paid_break_max_minutes ?? ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setForm({
                          ...form,
                          paid_break_max_minutes: raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0),
                        });
                      }}
                    />
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      Breaks up to this many minutes are paid; anything longer is unpaid and deducted from the timesheet.
                    </div>
                  </FormField>

                  {/* Rule-based break defaults — iteration 200 */}
                  <div className="mt-3 pt-3 border-t border-[#27272A]/60 space-y-2">
                    <div className="label-mute">Rule-Based Break Defaults (Payroll)</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Applied to every attendance row at this site — no  start/stop required.
                      Only applied when the shift meets the threshold below.
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <FormField label="Default paid break (min)">
                        <input
                          data-testid="site-default-paid-break-min"
                          type="number"
                          min="0"
                          step="1"
                          className={inputClass}
                          placeholder="0"
                          value={form.default_paid_break_minutes ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setForm({
                              ...form,
                              default_paid_break_minutes: raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0),
                            });
                          }}
                        />
                      </FormField>
                      <FormField label="Default unpaid break (min)">
                        <input
                          data-testid="site-default-unpaid-break-min"
                          type="number"
                          min="0"
                          step="1"
                          className={inputClass}
                          placeholder="0"
                          value={form.default_unpaid_break_minutes ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setForm({
                              ...form,
                              default_unpaid_break_minutes: raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0),
                            });
                          }}
                        />
                      </FormField>
                      <FormField label="Threshold (hours)">
                        <input
                          data-testid="site-break-threshold-hours"
                          type="number"
                          min="0"
                          step="0.5"
                          className={inputClass}
                          placeholder="6"
                          value={form.break_deduction_threshold_hours ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setForm({
                              ...form,
                              break_deduction_threshold_hours: raw === "" ? null : Math.max(0, parseFloat(raw) || 0),
                            });
                          }}
                        />
                      </FormField>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Adjusted Worked Time = Total − Default Unpaid Break Hours (when shift ≥ threshold, default 6h).
                    </div>
                  </div>
                </div>

                {/* Auto-start shifts — iteration 204 */}
                <div className="mt-4 border-t border-[#27272A] pt-3 space-y-2">
                  <div className="label-mute">Auto-Start Shifts</div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      data-testid="site-auto-start-shifts"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      checked={!!form.auto_start_shifts}
                      onChange={(e) => setForm({ ...form, auto_start_shifts: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      Auto-start every assigned shift at this site
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        The system clocks assigned  in at the shift&rsquo;s scheduled start and closes it at the end. Tardy alerts are suppressed. The officer can still show up and clock in — their record overrides the auto-start.
                      </span>
                    </span>
                  </label>
                  <FormField label="Delay minutes (0 = fire at exact start)">
                    <input
                      data-testid="site-auto-start-delay-min"
                      type="number"
                      min="0"
                      max="120"
                      step="1"
                      className={inputClass}
                      placeholder="0"
                      disabled={!form.auto_start_shifts}
                      value={form.auto_start_delay_minutes ?? 0}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setForm({
                          ...form,
                          auto_start_delay_minutes: raw === "" ? 0 : Math.max(0, Math.min(120, parseInt(raw, 10) || 0)),
                        });
                      }}
                    />
                  </FormField>
                </div>

                {/* Missed tour stops watchdog — iter 208 */}
                <div className="mt-4 border-t border-[#27272A] pt-3 space-y-2">
                  <div className="label-mute">DAR Mode</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 items-start">
                    <FormField label="Mode">
                      <select
                        data-testid="site-dar-mode"
                        className={inputClass}
                        value={form.dar_mode || "default"}
                        onChange={(e) => setForm({ ...form, dar_mode: e.target.value })}
                      >
                        <option value="default">Default — free-form narrative</option>
                        <option value="detailed">Detailed — photo per scan + auto table</option>
                      </select>
                    </FormField>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-6 leading-relaxed">
                      Detailed mode requires a photo on every tour scan and auto-populates
                      the DAR with a table (tag · description · photo · timestamp). Free-form
                      narrative is still available but collapsed by default.
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#27272A] pt-3 space-y-2">
                  <div className="label-mute">Missed Tour Stops — Watchdog</div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      data-testid="site-missed-tour-enabled"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      checked={!!form.missed_tour_check_enabled}
                      onChange={(e) => setForm({ ...form, missed_tour_check_enabled: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      Alert when a &rsquo;s tour scanning drops below expected coverage
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        A background sweep runs during every in-progress shift after clock-in. Every <em>interval</em> minutes it counts NEW scans since the previous window and, if the threshold is missed, notifies the tardy list plus the dedicated addresses below.
                      </span>
                    </span>
                  </label>
                  <div className="grid md:grid-cols-3 gap-3">
                    <FormField label="Check interval (min)">
                      <input
                        data-testid="site-missed-tour-interval"
                        type="number"
                        min="5"
                        max="240"
                        step="1"
                        className={inputClass}
                        placeholder="30"
                        disabled={!form.missed_tour_check_enabled}
                        value={form.missed_tour_check_interval_minutes ?? 30}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setForm({
                            ...form,
                            missed_tour_check_interval_minutes: raw === "" ? 30 : Math.max(5, Math.min(240, parseInt(raw, 10) || 30)),
                          });
                        }}
                      />
                    </FormField>
                    <FormField label="Min scans per interval">
                      <input
                        data-testid="site-missed-tour-min-scans"
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        className={inputClass}
                        placeholder="0"
                        disabled={!form.missed_tour_check_enabled}
                        value={form.missed_tour_min_scans_per_window ?? 0}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setForm({
                            ...form,
                            missed_tour_min_scans_per_window: raw === "" ? 0 : Math.max(0, Math.min(50, parseInt(raw, 10) || 0)),
                          });
                        }}
                      />
                    </FormField>
                    <FormField label="Alert on zero scans">
                      <label className="flex items-center gap-2 h-[38px] cursor-pointer select-none">
                        <input
                          data-testid="site-missed-tour-alert-on-zero"
                          type="checkbox"
                          className="accent-[#FF3B30]"
                          disabled={!form.missed_tour_check_enabled}
                          checked={!!form.missed_tour_alert_on_zero}
                          onChange={(e) => setForm({ ...form, missed_tour_alert_on_zero: e.target.checked })}
                        />
                        <span className="text-xs text-zinc-400">Fire when window has 0 scans</span>
                      </label>
                    </FormField>
                  </div>
                  <FormField label="Notification emails (comma or newline separated)">
                    <EmailListInput
                      testId="site-missed-tour-emails"
                      multiline
                      rows={2}
                      className={inputClass + " font-mono text-xs"}
                      placeholder="ops@example.com, dispatch@example.com"
                      disabled={!form.missed_tour_check_enabled}
                      value={form.missed_tour_notification_emails}
                      onChange={(list) =>
                        setForm({ ...form, missed_tour_notification_emails: list })
                      }
                    />
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      Sent in addition to the site&rsquo;s tardy list, supervisors and notified portal users.
                    </div>
                  </FormField>
                </div>

                {/* Mobile-app site policy — Ad-hoc + unverified clock-in */}
                <div className="mt-4 border-t border-[#27272A] pt-3 space-y-2">
                  <div className="label-mute">Mobile App Policy</div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      data-testid="site-allow-adhoc"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      checked={!!form.allow_adhoc_shifts}
                      onChange={(e) => setForm({ ...form, allow_adhoc_shifts: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      Allow ad-hoc shift start
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        Officers inside the geofence can start a shift here without pre-scheduling. They pick the end time.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      data-testid="site-allow-unverified-clockin"
                      type="checkbox"
                      className="mt-0.5 accent-[#FF3B30]"
                      checked={!!form.allow_unverified_clock_in}
                      onChange={(e) => setForm({ ...form, allow_unverified_clock_in: e.target.checked })}
                    />
                    <span className="text-sm text-zinc-300">
                      Allow clock-in with unverified location
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        For indoor / low-signal sites. Attendance is stamped <code>location_verified=false</code> for audit.
                      </span>
                    </span>
                  </label>
                  {/* Per-site auto-approve override — tri-state select */}
                  <div className="pt-2">
                    <div className="label-mute mb-1">Auto-approve ad-hoc shifts (site override)</div>
                    <select
                      data-testid="site-auto-approve-adhoc"
                      className={inputClass}
                      value={form.auto_approve_adhoc === null || form.auto_approve_adhoc === undefined ? "inherit" : (form.auto_approve_adhoc ? "enabled" : "disabled")}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm({ ...form, auto_approve_adhoc: v === "inherit" ? null : v === "enabled" });
                      }}
                      disabled={!form.allow_adhoc_shifts}
                    >
                      <option value="inherit">Inherit agency default</option>
                      <option value="enabled">Enabled — skip audit queue when criteria met</option>
                      <option value="disabled">Disabled — always require manual approval</option>
                    </select>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      Criteria (all must hold):  inside geofence · ≥1 prior shift at site · duration ≤ agency cap (default 12h).
                    </div>
                  </div>
                </div>
                {(form.tardy_notifications_enabled || form.geofence_notifications_enabled) && (
                  <div className="mt-4 space-y-3">
                    <FormField label="Additional email recipients (comma-separated)">
                      <EmailListInput
                        testId="site-tardy-notif-emails"
                        className={inputClass}
                        placeholder="ops@client.com, area.manager@client.com"
                        value={form.tardy_notification_emails}
                        onChange={(list) =>
                          setForm({ ...form, tardy_notification_emails: list })
                        }
                      />
                    </FormField>
                    <FormField label="Also notify portal users on this client">
                      <MultiSelectDropdown
                        testId="site-notify-portal-users"
                        label={form.client_id ? "No portal users selected" : "Pick a client first"}
                        options={portalUsers
                          .filter((u) => !form.client_id || u.client_id === form.client_id)
                          .map((u) => ({
                            value: u.id,
                            label: u.name || u.email,
                            sub: `${u.email} · ${(u.role || "").replace("_", " ")}`,
                          }))}
                        selected={form.notify_portal_user_ids || []}
                        onChange={(v) => setForm({ ...form, notify_portal_user_ids: v })}
                        searchable
                        triggerClassName="w-full"
                        emptyHint={form.client_id ? "No portal users on this client yet" : "Pick a client first"}
                      />
                    </FormField>
                  </div>
                )}
              </div>
              <div className="col-span-2 border-t border-[#27272A] pt-3" data-testid="site-reports-config">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="label-mute flex items-center gap-1.5">Reports enabled for this site</div>
                  {/* Master toggle: reflects the current 4-checkbox state and lets the admin flip all in one click.
                      "All / None / Mixed" is derived — clicking it cycles All ⇄ None; Mixed always resets to All. */}
                  {(() => {
                    const cfg = form.enabled_reports || { incidents: true, dar: true, maintenance: true, tours: true };
                    const values = ["incidents", "dar", "maintenance", "tours"].map((k) => cfg[k] !== false);
                    const allOn = values.every(Boolean);
                    const noneOn = values.every((v) => !v);
                    const label = allOn ? "Disable all" : noneOn ? "Enable all" : "Enable all";
                    const nextState = !allOn;  // Mixed or noneOn → true (enable all); allOn → false (disable all)
                    return (
                      <button
                        type="button"
                        data-testid="site-reports-master-toggle"
                        onClick={() => setForm({
                          ...form,
                          enabled_reports: {
                            incidents: nextState, dar: nextState, maintenance: nextState, tours: nextState,
                          },
                        })}
                        className="text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A] border border-[#27272A] hover:border-[#FF3B30] px-2 py-0.5 transition-colors"
                      >
                        {label}
                        <span className="ml-2 text-zinc-500">
                          ({allOn ? "all on" : noneOn ? "all off" : "mixed"})
                        </span>
                      </button>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    ["incidents", "Incident Reports"],
                    ["dar", "Daily Activity Reports"],
                    ["maintenance", "Maintenance Requests"],
                    ["tours", "Tour Stop Scans"],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        data-testid={`site-report-enabled-${key}`}
                        type="checkbox"
                        className="accent-[#FF3B30]"
                        checked={form.enabled_reports?.[key] !== false}
                        onChange={(e) => setForm({
                          ...form,
                          enabled_reports: {
                            ...(form.enabled_reports || { incidents: true, dar: true, maintenance: true, tours: true }),
                            [key]: e.target.checked,
                          },
                        })}
                      />
                      <span className="text-zinc-300">{label}</span>
                    </label>
                  ))}
                </div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
                  <input
                    data-testid="site-notify-portal-users-on-reports"
                    type="checkbox"
                    className="mt-0.5 accent-[#FF3B30]"
                    checked={!!form.notify_portal_users_on_reports}
                    onChange={(e) => setForm({ ...form, notify_portal_users_on_reports: e.target.checked })}
                  />
                  <span className="text-sm text-zinc-300">
                    Email portal users on new reports for this site
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Sends a light-mode email to every portal user assigned to this site when Incident / DAR / Maintenance reports are filed.
                    </span>
                  </span>
                </label>

                {/* iter236 — per-site email content limits (optional overrides) */}
                {form.notify_portal_users_on_reports && (
                  <div className="mt-4 pt-3 border-t border-[#1c1c1e] grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="site-email-limits-overrides">
                    <FormField
                      label={<>Scan-log rows <span className="text-zinc-500 normal-case tracking-normal">(Detailed DAR)</span></>}
                    >
                      <input
                        type="number"
                        min="1"
                        max="50"
                        step="1"
                        placeholder="agency default"
                        data-testid="site-email-scan-log-limit"
                        className={inputClass}
                        value={form.email_scan_log_limit ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setForm({
                            ...form,
                            email_scan_log_limit: raw === "" ? null : Math.min(50, Math.max(1, parseInt(raw, 10) || 1)),
                          });
                        }}
                      />
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 block">
                        Leave blank to inherit agency setting. 1&ndash;50 rows.
                      </span>
                    </FormField>
                    <FormField
                      label={<>Photos per report <span className="text-zinc-500 normal-case tracking-normal">(all report kinds)</span></>}
                    >
                      <input
                        type="number"
                        min="1"
                        max="20"
                        step="1"
                        placeholder="agency default"
                        data-testid="site-email-photo-limit"
                        className={inputClass}
                        value={form.email_photo_limit ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setForm({
                            ...form,
                            email_photo_limit: raw === "" ? null : Math.min(20, Math.max(1, parseInt(raw, 10) || 1)),
                          });
                        }}
                      />
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 block">
                        Leave blank to inherit. 1&ndash;20. First 8 shown full-size, rest as thumbnails.
                      </span>
                    </FormField>
                  </div>
                )}
              </div>
              <div className="col-span-2 border-t border-[#27272A] pt-3" data-testid="site-weekly-digest-section">
                <div className="label-mute mb-2 flex items-center gap-1.5">Weekly AI Digest</div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    data-testid="site-weekly-digest-enabled"
                    type="checkbox"
                    className="mt-0.5 accent-[#FF3B30]"
                    checked={!!form.weekly_digest_enabled}
                    onChange={(e) => setForm({ ...form, weekly_digest_enabled: e.target.checked })}
                  />
                  <span className="text-sm text-zinc-300">
                    Send weekly digest email every Monday
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      Recaps the past Sun→Sat window with every DAR + incident AI summary. Skips quiet weeks automatically.
                    </span>
                  </span>
                </label>
                {form.weekly_digest_enabled && (
                  <div className="mt-3 space-y-3">
                    <FormField label="Recipients (comma-separated emails)">
                      <EmailListInput
                        testId="site-weekly-digest-recipients"
                        className={inputClass}
                        placeholder="ops@client.com, area.manager@client.com"
                        value={form.weekly_digest_recipients}
                        onChange={(list) =>
                          setForm({ ...form, weekly_digest_recipients: list })
                        }
                      />
                    </FormField>
                    {editing?.id && (
                      <div className="flex items-center gap-2">
                        <GhostButton
                          data-testid="site-weekly-digest-preview-me"
                          onClick={async () => {
                            try {
                              await api.post("/system/weekly-digest/preview-email", { site_id: editing.id });
                              toast.success("Preview digest sent to your email");
                            } catch (err) {
                              toast.error(errorMessage(err));
                            }
                          }}
                        >
                          Send Preview to Me
                        </GhostButton>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Emails a preview of last week&apos;s digest to your address
                        </span>
                      </div>
                    )}
                    {!editing?.id && (
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        Save the site first to enable the preview button.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <FormField label="Post Orders (instructions for  on site)">
                  <textarea data-testid="site-post-orders-input" rows={3} className={inputClass} value={form.post_orders_text || ""} onChange={(e) => setForm({ ...form, post_orders_text: e.target.value })} placeholder="Describe what is expected at the site — access codes, patrol routine, points of contact, escalation rules…" />
                </FormField>
              </div>
              <div className="col-span-2 border-t border-[#27272A] pt-3">
                <div className="label-mute mb-2 flex items-center gap-1.5"><ScrollText className="size-3" /> Access & Contact Details</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Gate Codes">
                    <input data-testid="site-gate-codes" className={inputClass} placeholder="Front #1234 · Back #5678"
                      value={form.gate_codes || ""} onChange={(e) => setForm({ ...form, gate_codes: e.target.value })} />
                  </FormField>
                  <FormField label="Lockbox Codes">
                    <input data-testid="site-lockbox-codes" className={inputClass} placeholder="Lobby lockbox 4-2-8-1"
                      value={form.lockbox_codes || ""} onChange={(e) => setForm({ ...form, lockbox_codes: e.target.value })} />
                  </FormField>
                  <FormField label="Access Fobs">
                    <input data-testid="site-access-fobs" className={inputClass} placeholder="2 fobs — labeled A, B"
                      value={form.access_fobs || ""} onChange={(e) => setForm({ ...form, access_fobs: e.target.value })} />
                  </FormField>
                  <FormField label="Keys Available">
                    <input data-testid="site-keys-available" className={inputClass} placeholder="Master + 2 room keys in drawer"
                      value={form.keys_available || ""} onChange={(e) => setForm({ ...form, keys_available: e.target.value })} />
                  </FormField>
                  <div className="col-span-2">
                    <FormField label="Point of Contact / Manager">
                      <textarea rows={2} data-testid="site-poc-manager" className={inputClass}
                        placeholder="Jane Smith · Property Manager · 555-1000 · jsmith@acme.com"
                        value={form.poc_manager_contact || ""} onChange={(e) => setForm({ ...form, poc_manager_contact: e.target.value })} />
                    </FormField>
                  </div>
                  <div className="col-span-2">
                    <FormField label="Other Notes">
                      <textarea rows={2} data-testid="site-post-orders-other" className={inputClass}
                        placeholder="Any additional access, escalation, or site quirks…"
                        value={form.post_orders_other || ""} onChange={(e) => setForm({ ...form, post_orders_other: e.target.value })} />
                    </FormField>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
            <ActionButton data-testid="save-site-button" onClick={onSave}>Save Site</ActionButton>
          </div>
        </Modal>
      )}

      {/* ── Officers-at-site modal ───────────────────────────────────── */}
      {guardsModalSite && (
        <Modal title={`Officers · ${guardsModalSite.name}`} onClose={() => setGuardsModalSite(null)} testId="site-guards-modal">
          <p className="text-xs text-zinc-500 mb-4">Officers assigned to shifts at this site within the last 30 days and the next 30 days.</p>
          {siteGuards.length === 0 ? (
            <div className="border border-[#27272A] p-8 text-center text-zinc-500">No  currently assigned.</div>
          ) : (
            <table className="w-full text-sm" data-testid="site-guards-table">
              <thead className="border-b border-[#27272A] label-mute">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Badge</th>
                  <th className="px-3 py-2 text-left">Phone</th>
                  <th className="px-3 py-2 text-left">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {siteGuards.map((g) => (
                  <tr key={g.id}>
                    <td className="px-3 py-2 font-medium">{g.name}</td>
                    <td className="px-3 py-2"><PillBadge tone="default">{g.role || "guard"}</PillBadge></td>
                    <td className="px-3 py-2 font-mono text-[#FF9F0A]">{g.badge_no}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400">{g.phone}</td>
                    <td className="px-3 py-2 font-mono text-zinc-400">{g.email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {/* ── Post Orders modal ──────────────────────────────────────── */}
      {postOrdersSite && (
        <Modal title={`Post Orders · ${postOrdersSite.name}`} onClose={() => setPostOrdersSite(null)} testId="post-orders-modal">
          {postOrdersSite.post_orders_updated_at && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
              last updated {new Date(postOrdersSite.post_orders_updated_at).toLocaleString()}
            </div>
          )}
          <div className="border border-[#27272A] bg-[#0A0A0A] p-4 text-sm whitespace-pre-wrap text-zinc-200 min-h-[120px]" data-testid="post-orders-text">
            {postOrdersSite.post_orders_text || <span className="text-zinc-500">No post orders text yet. Use Edit Site to add instructions.</span>}
          </div>
          {/* Structured access & contact details — only shown when at least one is set */}
          {(postOrdersSite.gate_codes || postOrdersSite.lockbox_codes ||
            postOrdersSite.access_fobs || postOrdersSite.keys_available ||
            postOrdersSite.poc_manager_contact || postOrdersSite.post_orders_other) && (
            <div className="mt-4 border-t border-[#27272A] pt-4" data-testid="post-orders-details">
              <div className="label-mute mb-3 flex items-center gap-2"><ScrollText className="size-3" /> Access & Contact</div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {[
                  ["Gate Codes",        postOrdersSite.gate_codes,          "post-orders-view-gate-codes"],
                  ["Lockbox Codes",     postOrdersSite.lockbox_codes,       "post-orders-view-lockbox-codes"],
                  ["Access Fobs",       postOrdersSite.access_fobs,         "post-orders-view-access-fobs"],
                  ["Keys Available",    postOrdersSite.keys_available,      "post-orders-view-keys"],
                  ["Manager / Contact", postOrdersSite.poc_manager_contact, "post-orders-view-manager"],
                  ["Other",             postOrdersSite.post_orders_other,   "post-orders-view-other"],
                ].filter(([, v]) => !!v).map(([k, v, testid]) => (
                  <div key={k} className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={testid}>
                    <dt className="label-mute mb-1">{k}</dt>
                    <dd className="whitespace-pre-wrap text-zinc-100 font-mono text-xs">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          <div className="mt-4 border-t border-[#27272A] pt-4">
            <div className="label-mute mb-2 flex items-center gap-2"><FileText className="size-3" /> Document</div>
            {postOrdersSite.post_orders_document ? (
              <div className="border border-[#27272A] p-3 flex items-center justify-between gap-3" data-testid="post-orders-doc">
                <div className="min-w-0 flex-1">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${postOrdersSite.post_orders_document.storage_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="post-orders-doc-link"
                    className="text-sm font-medium text-[#0A84FF] hover:underline truncate flex items-center gap-1"
                  >
                    <ExternalLink className="size-3 shrink-0" /> {postOrdersSite.post_orders_document.name}
                  </a>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    uploaded {new Date(postOrdersSite.post_orders_document.uploaded_at).toLocaleString()}
                    {postOrdersSite.post_orders_document.size && ` · ${(postOrdersSite.post_orders_document.size / 1024).toFixed(1)} KB`}
                  </div>
                </div>
                <GhostButton data-testid="post-orders-replace-button" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="size-3.5" /> Replace
                </GhostButton>
              </div>
            ) : (
              <div className="border border-dashed border-[#27272A] p-4 flex items-center justify-between gap-3">
                <div className="text-xs text-zinc-500">No document uploaded yet. PDF, Word, image, or plain text accepted.</div>
                <ActionButton data-testid="post-orders-upload-button" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="size-3.5" /> Upload
                </ActionButton>
              </div>
            )}
            <input
              ref={fileInputRef}
              data-testid="post-orders-upload-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
              onChange={(e) => uploadPostOrdersDoc(e, postOrdersSite.id)}
              className="hidden"
            />
          </div>
        </Modal>
      )}

      {tourStopsSite && (
        <TourStopsModal site={tourStopsSite} onClose={() => setTourStopsSite(null)} />
      )}

      {geocodeAllResult && (
        <GeocodeResultsModal result={geocodeAllResult} onClose={() => setGeocodeAllResult(null)} />
      )}
      {deactivateTarget && (
        <SiteDeactivateModal
          site={deactivateTarget}
          status={deactivateStatus}
          onStatusChange={setDeactivateStatus}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={confirmDeactivate}
        />
      )}
    </div>
  );
}


/**
 * TempJobSection — toggle-driven header on the Site form that turns a
 * regular site into a temp job (event, HQ move, film shoot, etc.).
 *
 * Two flavours (see /app/memory/PRD.md · Temp Jobs · §1):
 *   ①  Ad-hoc requester (walk-in) — no underlying client record.
 *   ②  Existing client, temp location — real client, temp site.
 *
 * When toggled ON:
 *   • Reveals a "Requester type" segmented control (client vs ad-hoc).
 *   • Reveals a required End date.
 *   • The parent form hides the ad-hoc / client picker per selection.
 */
function TempJobSection({ form, setForm, adhocRequesters }) {
  const active = adhocRequesters.filter((r) => (r.status || "active") === "active");
  const isAdhoc = !!form.adhoc_requester_id;
  return (
    <div
      className={`border p-4 ${form.is_temporary ? "border-[#FF9F0A] bg-[#FF9F0A]/[0.04]" : "border-[#27272A] bg-[#0A0A0A]"}`}
      data-testid="site-temp-job-section"
    >
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-[#FF9F0A] size-4"
          checked={!!form.is_temporary}
          onChange={(e) => {
            const on = e.target.checked;
            setForm((f) => ({
              ...f,
              is_temporary: on,
              // Reset temp-only fields when disabling.
              temp_end_date: on ? f.temp_end_date : "",
              adhoc_requester_id: on ? f.adhoc_requester_id : "",
              portal_visible: on ? f.portal_visible : true,
            }));
          }}
          data-testid="site-is-temporary"
        />
        <div>
          <div className="text-sm font-medium text-zinc-100">Temporary / one-off job</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">
            Event, HQ move, film shoot — auto-archives 48h after the end date
          </div>
        </div>
      </label>

      {form.is_temporary && (
        <div className="mt-4 space-y-4">
          {/* Requester type segmented control */}
          <div>
            <div className="label-mute mb-2">Requester type</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, adhoc_requester_id: "" }))}
                className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${
                  !isAdhoc
                    ? "border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/10"
                    : "border-[#27272A] text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid="site-temp-type-client"
              >
                Existing client
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, client_id: "", adhoc_requester_id: active[0]?.id || "" }))}
                disabled={active.length === 0}
                className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isAdhoc
                    ? "border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/10"
                    : "border-[#27272A] text-zinc-500 hover:text-zinc-300"
                }`}
                title={active.length === 0 ? "No ad-hoc requesters yet — create one from Settings → Ad-hoc Requesters" : ""}
                data-testid="site-temp-type-adhoc"
              >
                Ad-hoc requester {active.length === 0 && "(none)"}
              </button>
            </div>
          </div>

          {isAdhoc && (
            <div>
              <div className="label-mute mb-2">Ad-hoc requester *</div>
              <select
                className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#FF9F0A]"
                value={form.adhoc_requester_id}
                onChange={(e) => setForm({ ...form, adhoc_requester_id: e.target.value })}
                data-testid="site-adhoc-requester-select"
              >
                <option value="">— choose a requester —</option>
                {active.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r.contact_name ? `· ${r.contact_name}` : ""}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-2">
                Manage ad-hoc requesters from <span className="text-[#FF9F0A]">Settings → Ad-hoc Requesters</span>.
              </div>
            </div>
          )}

          {/* Required end date */}
          <div>
            <div className="label-mute mb-2">Job end date *</div>
            <input
              type="date"
              className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#FF9F0A]"
              value={form.temp_end_date || ""}
              onChange={(e) => setForm({ ...form, temp_end_date: e.target.value })}
              data-testid="site-temp-end-date"
            />
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
              Site auto-archives 48h after this date. Reopen anytime from the site row.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

